import { useState } from "react";
import { Loader2, LogOut, MailCheck, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { errorMessage, post } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { usePageTitle } from "@/components/States";
import { Button } from "@/components/ui/button";

export const ConfirmEmailScreen = () => {
  const { profile, refreshProfile, signOut } = useAuth();
  const [busy, setBusy] = useState(null);
  usePageTitle("Confirm your email");

  const act = async (kind) => {
    setBusy(kind);
    try {
      if (kind === "resend") {
        const r = await post("/auth/resend-verification");
        if (r.already_verified) await refreshProfile();
        else toast.success(`We've sent a new link to ${profile.email}`);
      } else {
        const p = await refreshProfile();
        if (p?.email_verified === false) toast.message("Still waiting for you to click the link in the email");
      }
    } catch (e) {
      toast.error(errorMessage(e));
    } finally {
      setBusy(null);
    }
  };

  return (
    <div className="flex min-h-screen items-center justify-center bg-slate-950 px-6" data-testid="confirm-email-screen">
      <div className="home-rise w-full max-w-md rounded-2xl bg-white p-8 shadow-xl">
        <span className="flex h-12 w-12 items-center justify-center rounded-xl bg-blue-50 text-blue-700"><MailCheck className="h-6 w-6" /></span>
        <h1 className="font-display mt-5 text-2xl font-extrabold text-slate-900">Confirm your email</h1>
        <p className="mt-2 text-sm text-slate-600">
          We sent a confirmation link to <strong className="text-slate-900" data-testid="confirm-email-address">{profile.email}</strong>. Click it to unlock Warrant Whizz. The link lasts 24 hours.
        </p>
        <p className="mt-2 text-xs text-slate-500">Can&apos;t find it? Check your spam or junk folder.</p>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button onClick={() => act("check")} disabled={!!busy} className="bg-blue-700 hover:bg-blue-800" data-testid="confirm-email-check-button">
            {busy === "check" ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-1 h-4 w-4" />} I&apos;ve confirmed
          </Button>
          <Button variant="outline" onClick={() => act("resend")} disabled={!!busy} data-testid="confirm-email-resend-button">
            {busy === "resend" && <Loader2 className="mr-1 h-4 w-4 animate-spin" />} Resend email
          </Button>
        </div>
        <button onClick={signOut} className="mt-6 flex items-center gap-2 text-sm text-slate-500 hover:text-slate-900" data-testid="confirm-email-signout-button">
          <LogOut className="h-4 w-4" /> Sign out
        </button>
      </div>
    </div>
  );
};
